/* @flow */

import React, { Component } from 'react';
import { concat, pullAt, set } from 'lodash/fp';
import color from 'color';
import glamorous from 'glamorous';
import Button from 'app/components/commons/Button';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import InputField from 'app/components/commons/InputField';
import Modal from 'app/components/commons/Modal';
import TextArea from 'app/components/commons/TextArea';
import { hover } from 'app/utils/cssUtils';

type Props = {
  defaultMessage?: string,
  errors: Array<*>,
  maxRecipients?: number,
  onCancel: () => void,
  onSubmit: Object => void,
  title: string,
};

type State = {
  message: string,
  recipients: Array<{ email: string, name: string }>,
  showErrorMessage: boolean,
};

const ButtonGeneric = glamorous('button', {
  withProps: {
    type: 'button',
  },
})({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '2em',
  minWidth: '1.2em',
  outline: 0,
  padding: 0,
  transition: 'color 0.2s',
});

const ButtonAdd = glamorous(ButtonGeneric)(({ theme }) => ({
  color: theme.color.primary,
  ...hover({
    color: color(theme.color.primary)
      .lighten(0.2)
      .string(),
  }),
}));

const ButtonRemove = glamorous(ButtonGeneric)(({ theme }) => ({
  color: theme.color.basis5,
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  ...hover({
    color: theme.color.warning,
  }),
}));

const InviteRow = glamorous.div({
  display: 'flex',
});

const InputSet = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexDirection: 'row',
  },
}));

const InputWrapper = glamorous.div({
  flexBasis: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  paddingLeft: 5,
  paddingRight: 5,
});

class EmailShareModal extends Component<Props, State> {
  props: Props;
  state: State;

  static defaultProps = {
    defaultMessage: '',
    maxRecipients: 5,
  };

  constructor(props: Props, ...args: Array<*>) {
    super(props, ...args);
    // $FlowIgnore
    this.state = {
      message: props.defaultMessage,
      recipients: [
        {
          email: '',
          name: '',
        },
      ],
      showErrorMessage: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.errors && nextProps.errors !== this.props.errors) {
      this.setState({
        showErrorMessage: true,
      });
    }
  }

  handleMessageChange = (value: string) => {
    this.setState({
      message: value,
      showErrorMessage: false,
    });
  };

  handleRecipientChange = (index: number, key: string, value: string) => {
    this.setState(state => ({
      recipients: set([index, key], value, state.recipients),
      showErrorMessage: false,
    }));
  };

  handleSubmit = () => {
    const { message, recipients } = this.state;
    this.props.onSubmit({
      recipients: recipients.filter(r => r.name && r.email),
      message,
    });
  };

  handleCancel = () => {
    this.setState({
      showErrorMessage: false,
    });
    this.props.onCancel();
  };

  addFriend = () => {
    this.setState(state => ({
      recipients: concat(state.recipients, {
        name: '',
        email: '',
      }),
    }));
  };

  removeFriend = (index: number) => {
    const { recipients } = this.state;

    if (recipients.length === 1) {
      this.setState({
        recipients: [
          {
            name: '',
            email: '',
          },
        ],
      });
    } else {
      this.setState(state => ({
        recipients: pullAt(index, state.recipients),
      }));
    }
  };

  render() {
    const { errors, maxRecipients, title } = this.props;
    const { message, recipients, showErrorMessage } = this.state;

    return (
      <Modal size="medium" onCancel={this.handleCancel} isOpen>
        <form>
          <h1 className="text-center"> {title} </h1>
          {recipients.map(({ name, email }, i) => (
            <InviteRow
              key={i} // eslint-disable-line react/no-array-index-key
            >
              <ButtonRemove onClick={() => this.removeFriend(i)}>
                <span className="ion-minus-circled" />
              </ButtonRemove>
              <InputSet>
                <InputWrapper>
                  <InputField
                    label="Friend's name"
                    name="name"
                    placeholder="Full name"
                    value={name}
                    onChange={value => this.handleRecipientChange(i, 'name', value)}
                    lightContent
                  />
                </InputWrapper>
                <InputWrapper>
                  <InputField
                    label="Friend's email address"
                    name="email"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={value => this.handleRecipientChange(i, 'email', value)}
                    lightContent
                  />
                </InputWrapper>
              </InputSet>
            </InviteRow>
          ))}
          {recipients.length < maxRecipients && (
            <Grid>
              <GridCol width="one-third">
                <ButtonAdd onClick={this.addFriend}>
                  <span className="ion-plus-circled" />
                </ButtonAdd>
              </GridCol>
            </Grid>
          )}
          <hr className="hr__dark" />
          <TextArea
            label="Additional Message"
            placeholder="Additional Message"
            cols={40}
            rows={4}
            name="message"
            value={message}
            size="small"
            onChange={value => this.handleMessageChange(value)}
            lightContent
          />
          {showErrorMessage && <FormValidationMessage message={errors} />}
          <Button
            block
            onClick={this.handleSubmit}
            disabled={recipients.every(r => !r.name || !r.email)}
          >
            Send Invite
          </Button>
          <Button block size="small" kind="cancel" onClick={this.handleCancel}>
            Cancel
          </Button>
        </form>
      </Modal>
    );
  }
}

export default EmailShareModal;
